import {
  CleanroomReturnEntry,
  CleanroomRestVialEntry,
  CleanroomSequenceOrderBatches,
} from '@/modules/cleanroom/hooks/use-cleanroom-sequence-batches'
import {
  CleanroomContext,
  ScannableItem,
  ScannableItemCategory,
  type WeightHistoryEntry,
} from '@/modules/cleanroom/state-machine'
import {buildSolventRequirements} from '@/modules/production/components/picking-list/utils'
import {CatalogType, PreparationType, type ProductionWeighingStep} from '@cypro/engine/database/schema'
import type {ProductionWeighing} from '@cypro/engine/mutators'
import {SubstanceResult} from '@cypro/engine/utils'
import {TFnType} from '@tolgee/react'

type AllocatedBatch = CleanroomSequenceOrderBatches['activeSubstances'][number]

const SOLUTION_ROLE_LABEL_KEY: Partial<Record<ScannableItemCategory, string>> = {
  carrier: 'for_compounding',
  solvents: 'for_reconstitution',
}

export function determineSequenceScannableItems(
  t: TFnType,
  batches: CleanroomSequenceOrderBatches,
  restVialEntries: CleanroomRestVialEntry[] = [],
  returnEntries: CleanroomReturnEntry[] = []
) {
  if (!batches) return []

  const hasCarrier = Boolean(batches.carrier?.length)
  const hasSolvent = Boolean(batches.solvents?.length)
  const labelSolutionRoles = hasCarrier && hasSolvent

  const batchItems = [
    ...(batches.activeSubstances || []).map(batch => buildCategoryScannableItem(batch, 'activeSubstances')),
    ...(batches.carrier || []).map(batch => buildCategoryScannableItem(batch, 'carrier')),
    ...(batches.solvents || []).map(batch => buildCategoryScannableItem(batch, 'solvents')),
  ]

  const consumableItems = (batches.consumables || []).map(buildBatchScannableItem)

  return [
    ...returnEntries.flatMap(buildReturnScannableItem),
    ...batchItems.map(({category, ...item}) => {
      const labelKey = labelSolutionRoles ? SOLUTION_ROLE_LABEL_KEY[category] : undefined
      return labelKey ? {...item, name: `${item.name} (${t(labelKey).toLowerCase()})`} : item
    }),
    ...buildSolventRequirementItems(t, batches),
    ...restVialEntries.flatMap(r => buildRestVialScannableItem(t, r)),
    ...consumableItems,
  ]
}

/**
 * Solvents are only allocated as batches when the stock check is enabled. With it
 * skipped the cleanroom still has to list the reconstitution solvents to draw, so
 * they are derived from the trade names and shown as display-only materials.
 */
function buildSolventRequirementItems(t: TFnType, batches: CleanroomSequenceOrderBatches): ScannableItem[] {
  const allocatedSolventDrugIds = new Set(
    (batches.solvents ?? [])
      .map(batch => batch.tradeName?.medicationProfile?.drugCatalogItemId)
      .filter((id): id is string => Boolean(id))
  )

  const activeSubstances = batches.activeSubstances ?? []
  // a rest vial is already a solution, so only fresh vials consume reconstitution solvent
  const sourceBatches = [...activeSubstances, ...(batches.carrier ?? []), ...(batches.solvents ?? [])]
  const requirements = buildSolventRequirements(
    activeSubstances.map(batch => ({batchId: batch.id, quantity: batch.allocatedPickingListQuantity || 0})),
    sourceBatches
  )

  const labelKey = batches.carrier?.length ? SOLUTION_ROLE_LABEL_KEY.solvents : undefined

  return requirements
    .filter(requirement => !allocatedSolventDrugIds.has(requirement.solutionDrugId))
    .map(requirement => ({
      id: `solvent-requirement-${requirement.solutionDrugId}`,
      name: labelKey ? `${requirement.name} (${t(labelKey).toLowerCase()})` : requirement.name,
      quantityLabel: `${requirement.amountMl} ml`,
      quantity: 1,
      lotNumber: '',
      expiresAt: 0,
      scannedAt: null,
      kind: 'solventRequirement',
      requiresScan: false,
      category: 'solvents',
    }))
}

function buildCategoryScannableItem(
  batch: AllocatedBatch,
  category: ScannableItemCategory
): ScannableItem & {category: ScannableItemCategory} {
  return {...buildBatchScannableItem(batch), id: `${batch.id}#${category}`, category}
}

/**
 * A return is a pre-prepared bag assigned as the starting material. It is scanned
 * like any other material, keyed by its origin order number.
 */
function buildReturnScannableItem(returnItem: CleanroomReturnEntry): ScannableItem[] {
  return [
    {
      id: returnItem.returnId,
      lotNumber: returnItem.orderNumber || returnItem.returnId,
      name: formatReturnItemName(returnItem),
      quantity: 1,
      expiresAt: returnItem.expiryDate ? new Date(returnItem.expiryDate).getTime() : 0,
      scannedAt: null,
      kind: 'return',
      requiresScan: true,
    },
  ]
}

function buildBatchScannableItem(batch: AllocatedBatch): ScannableItem {
  const catalogType = batch.tradeName?.medicationProfile?.drugCatalogItem?.catalogType
  const kind =
    catalogType === CatalogType.enum.SOLUTION
      ? 'carrier'
      : catalogType === CatalogType.enum.ACTIVE_SUBSTANCE
        ? 'activeSubstance'
        : 'consumable'

  return {
    id: batch.id,
    batchId: batch.id,
    lotNumber: batch.lotNumber,
    name: batch.tradeName?.name || batch.consumable?.name || '',
    quantity: batch.allocatedPickingListQuantity || 0,
    expiresAt: batch.expiryDate,
    scannedAt: null,
    kind,
    requiresScan: kind !== 'consumable',
  }
}

function buildRestVialScannableItem(t: TFnType, restVial: CleanroomRestVialEntry): ScannableItem[] {
  if (!restVial.batch) return []

  const tn = restVial.batch.tradeName
  const baseName = tn?.name || restVial.tradeNameName

  return [
    {
      id: restVial.restVialId,
      lotNumber: restVial.batch.lotNumber,
      name: formatRestVialDisplayName(t, baseName, restVial.internalId),
      quantity: 1,
      expiresAt: new Date(restVial.expiryDate).getTime() || 0,
      scannedAt: null,
      kind: 'restVial',
      requiresScan: true,
    },
  ]
}

export function formatRestVialDisplayName(t: TFnType, name: string, internalId: number): string {
  return `${name} (${t('rest_vial').toLowerCase()} #${internalId})`
}

// function formatRestVialAmount(t: TFnType, amount: number, doseUnit?: string): string {
//   return `(${doseUnit ? `${amount}${doseUnit}` : `${amount}`} ${t('remaining').toLowerCase()})`
// }

/**
 * The solvent row the cleanroom is currently drawing/injecting, honouring the
 * machine's `currentSolventIndex` for substances with several solvents.
 */
export function getCompoundingCurrentSubstanceSolvent(
  substance: SubstanceResult | null | undefined,
  solventIndex = 0
): {name: string; volumeMl: number; weightGrams: number} | null {
  if (!substance?.reconstitution?.required) return null

  const solventRows = substance.reconstitution.solvents
  if (solventRows?.length) {
    const row = solventRows[solventIndex] ?? solventRows[0]
    return {
      name: row.name,
      volumeMl: row.drawVolumeMl ?? row.volumeMl,
      weightGrams: row.drawWeightGrams ?? row.weightGrams,
    }
  }

  // legacy single-solvent payload
  return {
    name: '',
    volumeMl: substance.reconstitution.solventDrawVolumeMl ?? substance.reconstitution.solventVolumeMl,
    weightGrams: substance.reconstitution.solventDrawWeightGrams ?? substance.reconstitution.solventWeightGrams,
  }
}

export function getCompoundingSubstanceResultSolventDrawVolumeMl(substance: SubstanceResult, solventIndex = 0) {
  return getCompoundingCurrentSubstanceSolvent(substance, solventIndex)?.volumeMl ?? 0
}

export function getCompoundingSubstanceResultSolventDrawWeightGrams(substance: SubstanceResult, solventIndex = 0) {
  return getCompoundingCurrentSubstanceSolvent(substance, solventIndex)?.weightGrams ?? 0
}

export function calculateWeightDifference(weightGrams: number, targetGrams: number) {
  const absWeightGrams = Math.abs(weightGrams)
  const absTarget = Math.abs(targetGrams)
  const diff = Math.abs(absWeightGrams - absTarget) / absTarget

  return diff
}

export function isWeightWithinTolerance(weightGrams: number, targetGrams: number, tolerance = 0.03) {
  if (targetGrams === 0) {
    const [lowerEnd, upperEnd] = [-tolerance * 100, tolerance * 100]
    return weightGrams > lowerEnd && weightGrams < upperEnd
  }

  const diff = calculateWeightDifference(weightGrams, targetGrams)

  return diff <= tolerance
}

/**
 * A tare watch must see the reading on the target's side: lifting the item gives a negative
 * reading, so a positive reading close to that target's absolute value is not the tare moment.
 */
export function isWeightAtTareTarget(weightGrams: number, targetGrams: number, tolerance = 0.03) {
  if (targetGrams !== 0 && Math.sign(weightGrams) !== Math.sign(targetGrams)) return false
  return isWeightWithinTolerance(weightGrams, targetGrams, tolerance)
}

export const ExpectedWeightGrams = {
  solvent(context: CleanroomContext) {
    const substance = context.targets?.substances[context.currentSubstanceIndex]
    if (!substance?.reconstitution.required) return null

    // with multiple solvents, the expected weight is the one of the solvent
    // currently being drawn; fall back to the legacy single volume otherwise
    const solventRows = substance.reconstitution.solvents?.length ? substance.reconstitution.solvents : null
    if (solventRows) {
      const row = solventRows[context.currentSolventIndex] ?? solventRows[0]
      return row.drawWeightGrams ?? row.weightGrams
    }
    const target = substance.reconstitution.solventDrawWeightGrams ?? substance.reconstitution.solventWeightGrams
    return target
  },

  currentDrug(context: CleanroomContext) {
    const substance = context.targets?.substances[context.currentSubstanceIndex]
    if (!substance) return null

    const target = substance.targetDrugWeightGrams
    return target
  },

  carrierPrimeSyringe(context: CleanroomContext) {
    if (context.targets?.carrierAction.type !== 'PRIME') return null

    const target = context.targets?.carrierAction.weightGrams
    return target
  },

  carrierPrimeContainer(context: CleanroomContext) {
    if (context.carrierBaselineWeightGrams == null || context.targets?.carrierAction.type !== 'PRIME') return null

    const target = context.carrierBaselineWeightGrams + context.targets?.carrierAction.weightGrams
    return target
  },

  withdrawal(context: CleanroomContext) {
    if (context.targets?.carrierAction.type !== 'WITHDRAW') return null

    // what leaves the bag is carrier, not drug: use the carrier weight from the engine
    return context.targets.carrierAction.weightGrams
  },

  finalContainer(context: CleanroomContext) {
    if (!context.targets || context.carrierBaselineWeightGrams == null) return null

    if (context.targets.carrierAction.type === 'PRIME') {
      // the pump holds the empty container, the primed carrier and the drug
      return (
        context.carrierBaselineWeightGrams +
        context.targets.carrierAction.weightGrams +
        context.targets.expectedFinalDrugWeightGrams
      )
    }

    // AD pre-filled tares again after the withdrawal, so the final reading is the injected drug
    return context.targets.expectedFinalDrugWeightGrams
  },

  topup(context: CleanroomContext) {
    if (context.targets?.carrierAction.type !== 'TOPUP') return null

    const target = context.targets?.carrierAction.weightGrams
    return target
  },

  drawnDrug(context: CleanroomContext) {
    if (context.targets?.carrierAction.type !== 'TOPUP') return null

    const target = context.targets?.carrierAction.weightGrams + context.targets?.expectedFinalDrugWeightGrams

    return target
  },

  all(context: CleanroomContext) {
    return {
      solvent: this.solvent(context),
      currentDrug: this.currentDrug(context),
      carrierPrimeSyringe: this.carrierPrimeSyringe(context),
      carrierPrimeContainer: this.carrierPrimeContainer(context),
      withdrawal: this.withdrawal(context),
      finalContainer: this.finalContainer(context),
      topup: this.topup(context),
      drawnDrug: this.drawnDrug(context),
    }
  },
}

export function formatReturnItemName(returnItem: CleanroomReturnEntry) {
  if (!returnItem) return ''
  return [returnItem.medicationName, returnItem.medicationForm].filter(Boolean).join(' - ')
}

export interface SubstanceMeta {
  medicineId: string | null
  allocatedDose: number
  /** the vial batch this allocation draws from */
  batchId: string | null
}

export function toProductionWeighings(
  history: WeightHistoryEntry[],
  meta: SubstanceMeta[],
  preparationType?: PreparationType
): ProductionWeighing[] {
  // volumetric steps are confirmed by hand and report 0 g: there are no real weighings
  if (preparationType === PreparationType.enum.VOLUMETRIC) return []

  return history.map(entry => {
    const substance = meta[entry.substanceIndex]
    return {
      step: entry.step,
      substanceIndex: entry.substanceIndex,
      medicineId: substance?.medicineId ?? null,
      allocatedDose: substance?.allocatedDose ?? null,
      expectedWeightGrams: entry.expectedWeightGrams,
      actualWeightGrams: entry.actualWeight,
      recordedAt: entry.recordedAt,
      batchId: substance?.batchId ?? null,
      scaleMemoryNumber: entry.scaleMemoryNumber,
      recordedBy: entry.recordedBy ?? null,
    }
  })
}

const EXPECTED_WEIGHT_BY_STEP: Record<ProductionWeighingStep, (context: CleanroomContext) => number | null> = {
  EMPTY_SYRINGE: () => null,
  CARRIER_BASELINE: () => null,
  EMPTY_CONTAINER: () => null,
  SOLVENT: context => ExpectedWeightGrams.solvent(context),
  DRUG: context => ExpectedWeightGrams.currentDrug(context),
  CARRIER_PRIME_SYRINGE: context => ExpectedWeightGrams.carrierPrimeSyringe(context),
  PRIMED_CONTAINER: context => ExpectedWeightGrams.carrierPrimeContainer(context),
  WITHDRAWAL: context => ExpectedWeightGrams.withdrawal(context),
  FINAL_CONTAINER: context => ExpectedWeightGrams.finalContainer(context),
  TOPUP: context => ExpectedWeightGrams.topup(context),
  DRAWN_DRUG: context => ExpectedWeightGrams.drawnDrug(context),
}

export function getExpectedWeightForStep(step: ProductionWeighingStep, context: CleanroomContext) {
  return EXPECTED_WEIGHT_BY_STEP[step](context) ?? null
}
